/*
 * Klaviyo Back in Stock
 * - Toggles the buy-buttons between "Add to cart" and "Notify me when available"
 *   based on the selected variant's availability (driven by the theme's variant-change event).
 * - Submits the shopper's email to Klaviyo's client Back in Stock endpoint, subscribing
 *   the profile to the Back in Stock flow for the selected variant.
 */
if (!customElements.get('back-in-stock-form')) {
  customElements.define(
    'back-in-stock-form',
    class BackInStockForm extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.publicKey = this.dataset.publicKey;
        this.variantId = this.dataset.variantId;

        this.form = this.querySelector('.back-in-stock-form__form');
        this.emailInput = this.querySelector('input[name="bis-email"]');
        this.submitBtn = this.querySelector('.back-in-stock-form__submit');
        this.errorEl = this.querySelector('[data-bis-error]');
        this.errorText = this.querySelector('.back-in-stock-form__error-text');
        this.successEl = this.querySelector('[data-bis-success]');
        this.variantLabel = this.querySelector('[data-bis-variant]');
        this.productTitle = this.dataset.productTitle || '';

        // The buy-buttons wrapper holds both the Add-to-cart submit and the Notify-me opener.
        // The modal lives outside the product form, so locate it via the section's submit button.
        const submitButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.buttonsWrapper = submitButton ? submitButton.closest('.product-form__buttons') : null;

        this.onSubmit = this.onSubmit.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
      }

      connectedCallback() {
        if (this.submitBtn) this.submitBtn.addEventListener('click', this.onSubmit);
        if (this.emailInput) this.emailInput.addEventListener('keydown', this.onKeydown);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const data = event && event.data;
            if (!data || String(data.sectionId) !== String(this.sectionId)) return;
            this.onVariantChange(data.variant);
          });
        }
      }

      disconnectedCallback() {
        if (this.submitBtn) this.submitBtn.removeEventListener('click', this.onSubmit);
        if (this.emailInput) this.emailInput.removeEventListener('keydown', this.onKeydown);
        this.variantChangeUnsubscriber?.();
      }

      onKeydown(event) {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.onSubmit(event);
        }
      }

      onVariantChange(variant) {
        if (!variant) return;

        this.variantId = variant.id;

        // Show "Notify me" only when the variant is genuinely unavailable.
        if (this.buttonsWrapper) {
          this.buttonsWrapper.classList.toggle('bis-active', variant.available === false);
        }

        // Keep the modal copy in sync with the selected variant.
        if (this.variantLabel && variant.title) {
          const variantSuffix = /default/i.test(variant.title) ? '' : ` - ${variant.title}`;
          this.variantLabel.textContent = `${this.productTitle}${variantSuffix}`;
        }

        // Reset any previous success/error state when the variant changes.
        this.reset();
      }

      reset() {
        this.hideError();
        if (this.successEl) this.successEl.hidden = true;
        if (this.form) this.form.hidden = false;
      }

      /**
       * Open the modal targeted at a specific variant (e.g. a sold-out size
       * clicked in the size dropdown), without changing the page's selection.
       */
      openFor(variantId, variantTitle) {
        if (variantId) this.variantId = variantId;

        if (this.variantLabel) {
          const suffix = variantTitle ? ` - ${variantTitle}` : '';
          this.variantLabel.textContent = `${this.productTitle}${suffix}`;
        }

        this.reset();

        const modal = this.closest('modal-dialog');
        if (modal && typeof modal.show === 'function') {
          modal.show(document.activeElement);
          if (this.emailInput) requestAnimationFrame(() => this.emailInput.focus());
        }
      }

      showError(message) {
        if (this.errorText) this.errorText.textContent = message;
        if (this.errorEl) this.errorEl.hidden = false;
      }

      hideError() {
        if (this.errorEl) this.errorEl.hidden = true;
        if (this.emailInput) this.emailInput.classList.remove('invalid');
      }

      setLoading(isLoading) {
        if (!this.submitBtn) return;
        this.submitBtn.classList.toggle('loading', isLoading);
        this.submitBtn.toggleAttribute('aria-busy', isLoading);
        this.submitBtn.disabled = isLoading;
      }

      onSubmit(event) {
        event.preventDefault();
        this.hideError();

        const email = this.emailInput ? this.emailInput.value.trim() : '';
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          if (this.emailInput) {
            this.emailInput.classList.add('invalid');
            this.emailInput.focus();
          }
          this.showError(window.theme?.variantStrings?.invalidEmail || 'Please enter a valid email address.');
          return;
        }

        if (!this.publicKey || !this.variantId) {
          this.showError('Something went wrong. Please try again.');
          return;
        }

        this.setLoading(true);

        // Klaviyo external catalog ID for a Shopify-synced variant.
        const catalogVariantId = `$shopify:::$default:::${this.variantId}`;

        fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(this.publicKey)}`, {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'content-type': 'application/json',
            revision: '2024-10-15',
          },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                profile: {
                  data: {
                    type: 'profile',
                    attributes: { email },
                  },
                },
                channels: ['EMAIL'],
              },
              relationships: {
                variant: {
                  data: {
                    type: 'catalog-variant',
                    id: catalogVariantId,
                  },
                },
              },
            },
          }),
        })
          .then((response) => {
            if (response.ok || response.status === 202) {
              this.onSuccess();
              return null;
            }
            return response
              .json()
              .catch(() => null)
              .then((body) => {
                const detail = body?.errors?.[0]?.detail || 'Something went wrong. Please try again.';
                this.showError(detail);
                this.setLoading(false);
              });
          })
          .catch(() => {
            this.showError('Something went wrong. Please try again.');
            this.setLoading(false);
          });
      }

      onSuccess() {
        this.setLoading(false);
        if (this.form) this.form.hidden = true;
        if (this.successEl) this.successEl.hidden = false;
      }
    }
  );
}
